import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import CryptoJS from "crypto-js";
import { getDecryptedUserFromCookie } from "../Components/utils/getCookie";
import { permissionForPath } from "../config/permissionMenuMap";

const STORAGE_KEY = "staff_permissions";

const PermissionContext = createContext({
  permissions: [],
  loading: false,
  isStaff: false,
  isAdmin: true,
  hasPermission: () => true,
  hasAnyPermission: () => true,
  canAccessPath: () => true,
  setPermissions: () => {},
  clearPermissions: () => {},
});

function decryptPermissionsBlob(encrypted) {
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, process.env.REACT_APP_CRYPTO_SECRET);
    const raw = bytes.toString(CryptoJS.enc.Utf8);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.permissions) ? parsed.permissions : parsed;
  } catch {
    return null;
  }
}

function loadStoredPermissions() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function PermissionProvider({ children }) {
  const user = getDecryptedUserFromCookie();
  const userType = String(user?.type ?? "").toLowerCase();
  const isStaff = userType === "staff";
  const isAdmin = !isStaff;

  const [permissions, setPermissionsState] = useState(() =>
    isStaff ? loadStoredPermissions() : []
  );

  const setPermissions = useCallback((next) => {
    const list = Array.isArray(next) ? next : [];
    setPermissionsState(list);
    if (list.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const clearPermissions = useCallback(() => {
    setPermissionsState([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const storeEncryptedPermissions = useCallback((encrypted) => {
    const list = decryptPermissionsBlob(encrypted);
    if (list) setPermissions(list);
  }, [setPermissions]);

  const hasPermission = useCallback(
    (slug) => {
      if (isAdmin) return true;
      if (!slug) return true;
      return permissions.includes(slug);
    },
    [isAdmin, permissions]
  );

  const hasAnyPermission = useCallback(
    (slugs = []) => {
      if (isAdmin) return true;
      if (!slugs.length) return true;
      return slugs.some((s) => permissions.includes(s));
    },
    [isAdmin, permissions]
  );

  const canAccessPath = useCallback(
    (pathname) => {
      if (isAdmin) return true;
      const required = permissionForPath(pathname);
      if (!required) return true;
      return hasPermission(required);
    },
    [isAdmin, hasPermission]
  );

  const value = useMemo(
    () => ({
      permissions,
      loading: false,
      isStaff,
      isAdmin,
      hasPermission,
      hasAnyPermission,
      canAccessPath,
      setPermissions,
      clearPermissions,
      storeEncryptedPermissions,
    }),
    [
      permissions,
      isStaff,
      isAdmin,
      hasPermission,
      hasAnyPermission,
      canAccessPath,
      setPermissions,
      clearPermissions,
      storeEncryptedPermissions,
    ]
  );

  return (
    <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionContext);
}

export default PermissionContext;
